// AI-CONTEXT: See docs/features/impact-per-euro-methodiek.md

/**
 * @typedef {object} StatusInput
 * @property {number} year - Boekjaar (= kalenderjaar, D3)
 * @property {boolean} confirmed - Kosten bevestigd conform vastgestelde jaarrekening
 * @property {number} [linkedMeasurementCount] - Aantal gekoppelde metingen in de periode (D5: participatieladder-periode-delta's + persona-gekoppelde MSI-delta's)
 * @property {Date} [now] - Peilmoment (injecteerbaar voor tests)
 */

/**
 * @typedef {object} StatusResultaat
 * @property {'definitief'|'voorlopig'|'onvoldoende'} status
 * @property {string|null} reden - Uitleg wanneer er geen (definitief) kengetal is
 * @property {number} periodeMaanden - Volle maanden die de meetperiode beslaat
 */

// Ondergrens uit methodiek § 5.2: met een handvol metingen bepaalt één casus de
// uitkomst (één participatieladder-traject van onder naar boven is al
// tienduizenden euro's).
const MINIMUM_GEKOPPELDE_METINGEN = 20

const MINIMUM_PERIODE_MAANDEN = 6

/**
 * Status van het kengetal voor een boekjaar (methodiek § 5.2):
 * - 'definitief'  — afgesloten boekjaar met kosten conform vastgestelde jaarrekening
 * - 'voorlopig'   — gematchte deelperiode van ≥ 6 maanden (of afgesloten jaar met
 *                   onbevestigde kosten uit de tussentijdse administratie)
 * - 'onvoldoende' — te weinig gekoppelde metingen of te korte periode: geen
 *                   kengetal tonen, wél de reden
 *
 * @param {StatusInput} input
 * @returns {StatusResultaat}
 */
const bepaalStatus = ({ year, confirmed, linkedMeasurementCount, now = new Date() } = {}) => {
  const jaar = Number(year)
  const huidigJaar = now.getFullYear()

  if (!Number.isFinite(jaar) || jaar > huidigJaar) {
    return { status: 'onvoldoende', reden: 'Geen geldig (begonnen) boekjaar.', periodeMaanden: 0 }
  }

  // Afgesloten boekjaar beslaat 12 maanden; van het lopende jaar tellen alleen
  // de volle maanden sinds 1 januari (getMonth() is 0-gebaseerd: juli → 6).
  const periodeMaanden = jaar < huidigJaar ? 12 : now.getMonth()

  const metingen = Number(linkedMeasurementCount)
  if (!Number.isFinite(metingen) || metingen < MINIMUM_GEKOPPELDE_METINGEN) {
    const huidig = Number.isFinite(metingen) ? Math.max(0, metingen) : 0
    const nogNodig = MINIMUM_GEKOPPELDE_METINGEN - huidig
    const koppelingWoord = nogNodig === 1 ? 'koppeling' : 'koppelingen'
    return {
      status: 'onvoldoende',
      reden: `Nog ${nogNodig} ${koppelingWoord} nodig in boekjaar ${jaar} (${huidig} van de ${MINIMUM_GEKOPPELDE_METINGEN}).`,
      periodeMaanden,
    }
  }

  if (periodeMaanden < MINIMUM_PERIODE_MAANDEN) {
    return {
      status: 'onvoldoende',
      reden: `De meetperiode is korter dan ${MINIMUM_PERIODE_MAANDEN} maanden.`,
      periodeMaanden,
    }
  }

  if (jaar < huidigJaar && confirmed === true) {
    return { status: 'definitief', reden: null, periodeMaanden }
  }

  return { status: 'voorlopig', reden: null, periodeMaanden }
}

export default bepaalStatus
export { MINIMUM_GEKOPPELDE_METINGEN, MINIMUM_PERIODE_MAANDEN }
